import { FC, useRef, useState } from 'react';
import { Typography, Box } from '@mui/material';

import { Rooftop, RooftopAuthorizedEmployer } from '@libs/models';
import {
  ROOFTOP_AUTHORIZED_REQUEST_INDEX_COLUMN_INITIAL_STATE,
  ROOFTOP_AUTHORIZED_REQUEST_INDEX_COLUMNS,
} from '@carcareers/utils';
import { DataGridProvider, useSnackbar } from '@carcareers/contexts';
import { DataGridFactory, AddAuthorizedEmployerModal } from '@carcareers/components';
import { useLazyRooftopAuthorizationFindAllQuery, useRooftopAuthorizationDeleteMutation } from '@carcareers/store';

interface RooftopAuthorizedRequestInputProps {
  rooftop?: Rooftop;
  hasActions?: boolean;
}

export const RooftopAuthorizedRequestInput: FC<RooftopAuthorizedRequestInputProps> = ({ rooftop, hasActions = false }) => {
  const { toggleSnackbar } = useSnackbar();
  const refreshGridRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const DataGrid = DataGridFactory<RooftopAuthorizedEmployer>({ fluidHeight: true, sx: { px: 0 } });
  const [trigger] = useLazyRooftopAuthorizationFindAllQuery();
  const [deleteAuthorization] = useRooftopAuthorizationDeleteMutation();

  const [open, setOpen] = useState(false);

  const handleDelete = async (id?: number) => {
    if (!id || !rooftop?.id) return;

    try {
      await deleteAuthorization({
        rooftopId: rooftop.id,
        authorizationId: id,
      }).unwrap();

      toggleSnackbar('success', 'Authorization removed successfully');
      refreshGridRef.current();
    } catch (error) {
      toggleSnackbar('error', 'Failed to remove authorization');
    }
  };

  const actions = {
    add: {
      label: 'Add Employer',
      onClick: () => setOpen(true),
    },
    other: {
      remove: {
        label: 'Remove Employer',
        onClick: handleDelete,
      },
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5">Authorized Employers</Typography>
      <DataGridProvider
        id={rooftop?.id}
        index={trigger} 
        onInit={(refresh) => {
          refreshGridRef.current = refresh;
        }}
      >
        <DataGrid
          columns={ROOFTOP_AUTHORIZED_REQUEST_INDEX_COLUMNS}
          columnInitialState={ROOFTOP_AUTHORIZED_REQUEST_INDEX_COLUMN_INITIAL_STATE}
          actions={hasActions ? actions : undefined}
        />
      </DataGridProvider>
      <AddAuthorizedEmployerModal 
        open={open} 
        onClose={() => setOpen(false)} 
        rooftop={rooftop} 
        onSuccess={() => refreshGridRef.current()}
      />
    </Box>
  );
};

export default RooftopAuthorizedRequestInput;
